import {
  documentPointToGradientPoint,
  gradientEndpointsForBounds,
  gradientPointToDocumentPoint,
  normalizeGradientPaint,
  radialGradientGeometryForBounds,
  type GradientPaint,
  type LinearGradientPaint,
  type RadialGradientPaint,
} from "@/document/model/styles";
import type { Rect } from "@/lib/math/rect";

export type GradientGizmoHandle =
  | { type: "start" }
  | { type: "end" }
  | { type: "center" }
  | { type: "radius" }
  | { type: "stop"; stopId: string };

export interface GradientGizmoGeometry {
  type: "linear";
  start: { x: number; y: number };
  end: { x: number; y: number };
  center: { x: number; y: number };
  stops: Array<{ id: string; x: number; y: number; anchorX: number; anchorY: number; position: number }>;
}

export interface RadialGradientGizmoGeometry {
  type: "radial";
  center: { x: number; y: number };
  radiusPoint: { x: number; y: number };
  radius: number;
  stops: Array<{ id: string; x: number; y: number; anchorX: number; anchorY: number; position: number }>;
}

export type AnyGradientGizmoGeometry = GradientGizmoGeometry | RadialGradientGizmoGeometry;

export function gradientGizmoGeometry(bounds: Rect, paint: GradientPaint, zoom = 1): AnyGradientGizmoGeometry {
  const normalized = normalizeGradientPaint(paint);
  if (normalized.type === "radialGradient") {
    return radialGizmoGeometry(bounds, normalized, zoom);
  }

  const vector = gradientEndpointsForBounds(bounds, normalized);
  const normal = gradientNormal(vector.start, vector.end);
  const offset = 18 / zoom;
  return {
    type: "linear",
    ...vector,
    stops: normalized.stops.map(stop => {
      const anchor = pointOnGradient(vector.start, vector.end, stop.position);
      return {
        id: stop.id,
        position: stop.position,
        anchorX: anchor.x,
        anchorY: anchor.y,
        x: anchor.x + normal.x * offset,
        y: anchor.y + normal.y * offset,
      };
    }),
  };
}

export function hitTestGradientGizmo(
  bounds: Rect,
  paint: GradientPaint,
  point: { x: number; y: number },
  zoom: number,
): GradientGizmoHandle | undefined {
  const geometry = gradientGizmoGeometry(bounds, paint, zoom);
  const endpointRadius = 8 / zoom;
  const stopRadius = 9 / zoom;

  for (const stop of [...geometry.stops].reverse()) {
    if (distance(point, stop) <= stopRadius) {
      return { type: "stop", stopId: stop.id };
    }
  }

  if (geometry.type === "radial") {
    if (distance(point, geometry.center) <= endpointRadius) {
      return { type: "center" };
    }

    if (distance(point, geometry.radiusPoint) <= endpointRadius) {
      return { type: "radius" };
    }

    return undefined;
  }

  if (distance(point, geometry.start) <= endpointRadius) {
    return { type: "start" };
  }

  if (distance(point, geometry.end) <= endpointRadius) {
    return { type: "end" };
  }

  return undefined;
}

export function stopPositionForPoint(bounds: Rect, paint: GradientPaint, point: { x: number; y: number }): number {
  const geometry = gradientGizmoGeometry(bounds, paint);
  if (geometry.type === "radial") {
    const axis = {
      x: geometry.radiusPoint.x - geometry.center.x,
      y: geometry.radiusPoint.y - geometry.center.y,
    };
    const axisLength = Math.max(0.0001, axis.x * axis.x + axis.y * axis.y);
    return clamp01(((point.x - geometry.center.x) * axis.x + (point.y - geometry.center.y) * axis.y) / axisLength);
  }

  const axis = {
    x: geometry.end.x - geometry.start.x,
    y: geometry.end.y - geometry.start.y,
  };
  const axisLength = Math.max(0.0001, axis.x * axis.x + axis.y * axis.y);
  return clamp01(((point.x - geometry.start.x) * axis.x + (point.y - geometry.start.y) * axis.y) / axisLength);
}

export function paintWithGradientAngle(paint: LinearGradientPaint, angle: number): LinearGradientPaint {
  return normalizeGradientPaint({
    ...paint,
    angle: normalizeAngle(angle),
    start: undefined,
    end: undefined,
  });
}

export function paintWithGradientEndpoint(
  paint: LinearGradientPaint,
  endpoint: "start" | "end",
  bounds: Rect,
  point: { x: number; y: number },
): LinearGradientPaint {
  const normalized = normalizeGradientPaint(paint);
  const geometry = gradientEndpointsForBounds(bounds, normalized);
  const start = endpoint === "start" ? point : geometry.start;
  const end = endpoint === "end" ? point : geometry.end;
  return normalizeGradientPaint({
    ...normalized,
    angle: angleFromEndpoints(start, end),
    start: documentPointToGradientPoint(bounds, start),
    end: documentPointToGradientPoint(bounds, end),
  });
}

export function paintWithGradientStopPosition(paint: LinearGradientPaint, stopId: string, position: number): LinearGradientPaint {
  return normalizeGradientPaint({
    ...paint,
    stops: normalizeGradientPaint(paint).stops.map(stop => stop.id === stopId ? { ...stop, position: clamp01(position) } : stop),
  });
}

export function paintWithRadialCenter(paint: RadialGradientPaint, bounds: Rect, point: { x: number; y: number }): RadialGradientPaint {
  const normalized = normalizeGradientPaint(paint);
  const geometry = radialGizmoGeometry(bounds, normalized);
  const dx = geometry.radiusPoint.x - geometry.center.x;
  const dy = geometry.radiusPoint.y - geometry.center.y;
  return normalizeGradientPaint({
    ...normalized,
    center: documentPointToGradientPoint(bounds, point),
    radiusPoint: documentPointToGradientPoint(bounds, { x: point.x + dx, y: point.y + dy }),
  });
}

export function paintWithRadialRadiusPoint(paint: RadialGradientPaint, bounds: Rect, point: { x: number; y: number }): RadialGradientPaint {
  const normalized = normalizeGradientPaint(paint);
  const geometry = radialGizmoGeometry(bounds, normalized);
  const radius = distance(point, geometry.center);
  return normalizeGradientPaint({
    ...normalized,
    radius: radius / Math.max(0.0001, Math.max(Math.abs(bounds.width), Math.abs(bounds.height))),
    radiusPoint: documentPointToGradientPoint(bounds, point),
  });
}

export function paintWithRadialStopPosition(paint: RadialGradientPaint, stopId: string, position: number): RadialGradientPaint {
  return normalizeGradientPaint({
    ...paint,
    stops: normalizeGradientPaint(paint).stops.map(stop => stop.id === stopId ? { ...stop, position: clamp01(position) } : stop),
  });
}

function radialGizmoGeometry(bounds: Rect, paint: RadialGradientPaint, zoom = 1): RadialGradientGizmoGeometry {
  const normalized = normalizeGradientPaint(paint);
  const geometry = radialGradientGeometryForBounds(bounds, normalized);
  const defaultRadiusPoint = { x: geometry.center.x, y: geometry.center.y + geometry.radius };
  const radiusPoint = normalized.radiusPoint ? gradientPointToDocumentPoint(bounds, normalized.radiusPoint) : defaultRadiusPoint;
  const direction = normalizedVector({
    x: radiusPoint.x - geometry.center.x,
    y: radiusPoint.y - geometry.center.y,
  }) ?? { x: 0, y: 1 };
  const normal = { x: -direction.y, y: direction.x };
  const offset = 18 / zoom;

  return {
    type: "radial",
    center: geometry.center,
    radiusPoint,
    radius: geometry.radius,
    stops: normalized.stops.map(stop => {
      const anchor = pointOnGradient(geometry.center, radiusPoint, stop.position);
      return {
        id: stop.id,
        position: stop.position,
        anchorX: anchor.x,
        anchorY: anchor.y,
        x: anchor.x + normal.x * offset,
        y: anchor.y + normal.y * offset,
      };
    }),
  };
}

function gradientNormal(start: { x: number; y: number }, end: { x: number; y: number }): { x: number; y: number } {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length <= 0.0001) {
    return { x: 0, y: -1 };
  }

  return {
    x: -dy / length,
    y: dx / length,
  };
}

function pointOnGradient(from: { x: number; y: number }, to: { x: number; y: number }, position: number): { x: number; y: number } {
  return {
    x: from.x + (to.x - from.x) * position,
    y: from.y + (to.y - from.y) * position,
  };
}

function normalizeAngle(angle: number): number {
  return ((angle % 360) + 360) % 360;
}

function angleFromEndpoints(start: { x: number; y: number }, end: { x: number; y: number }): number {
  return normalizeAngle(Math.atan2(end.y - start.y, end.x - start.x) * 180 / Math.PI);
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}

function distance(from: { x: number; y: number }, to: { x: number; y: number }): number {
  return Math.hypot(from.x - to.x, from.y - to.y);
}

function normalizedVector(vector: { x: number; y: number }): { x: number; y: number } | undefined {
  const length = Math.hypot(vector.x, vector.y);
  if (length <= 0.0001) {
    return undefined;
  }

  return {
    x: vector.x / length,
    y: vector.y / length,
  };
}
